"use client";

import * as React from "react";
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * v1's trigger had NO focus style at all. It now inherits the global `:focus-visible` outline,
 * and the trigger is a full-height 44px+ target.
 *
 * The affordance is a plus that rotates into a minus — a plus reads as "there is more here"
 * where a chevron only reads as "direction".
 */
export function Accordion({ items }: { items: { question: string; answer: string }[] }) {
  const [openIndex, setOpenIndex] = React.useState<number | null>(0);

  return (
    <div className="divide-y divide-edge border-y border-edge">
      {items.map((item, index) => {
        const isOpen = openIndex === index;
        const panelId = `faq-panel-${index}`;
        const buttonId = `faq-button-${index}`;
        return (
          <div key={item.question}>
            <h3>
              <button
                id={buttonId}
                type="button"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpenIndex(isOpen ? null : index)}
                className="flex w-full items-center justify-between gap-6 py-5 text-left text-body-l font-medium text-ink transition-colors duration-150 ease-standard hover:text-signal"
              >
                {item.question}
                <Plus
                  className={cn(
                    "h-5 w-5 shrink-0 text-muted transition-transform duration-150 ease-standard",
                    isOpen && "rotate-45",
                  )}
                  aria-hidden="true"
                />
              </button>
            </h3>
            <div
              id={panelId}
              role="region"
              aria-labelledby={buttonId}
              hidden={!isOpen}
              className="prose-column pb-6 text-body text-ink-2"
            >
              {item.answer}
            </div>
          </div>
        );
      })}
    </div>
  );
}
